import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { FONT_MAIN, glassCard, inputClass, inputStyle } from "./MobileFamiliares";
import { ArrowLeft, Building2, Car, ChevronDown, Loader2, Pencil, RefreshCw, Trash2, X } from "lucide-react";

const cpfValido = (cpf) => {
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dig = (base) => {
    let s = 0;
    for (let i = 0; i < base.length; i++) s += Number(base[i]) * (base.length + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dig(d.slice(0, 9)) === Number(d[9]) && dig(d.slice(0, 10)) === Number(d[10]);
};

const fmtCpf = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const fmtCelular = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const fmtDDI = (v) => {
  let d = v.replace(/[^\d+]/g, "");
  if (!d.startsWith("+")) d = `+${d.replace(/\+/g, "")}`;
  return `+${d.slice(1, 4)}`;
};

const p2 = (n) => String(n).padStart(2, "0");
const agoraLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
};
const hojeFim = () => {
  const d = new Date();
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T23:59`;
};
const fmtPeriodo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};
const isoParaInput = (iso) => (iso ? String(iso).slice(0, 16) : "");

const parseDataLocal = (v) => {
  if (!v) return null;
  const d = new Date(String(v).trim().replace(" ", "T"));
  return isNaN(d) ? null : d;
};
const statusPeriodo = (item) => {
  const ini = parseDataLocal(item.permanencia_inicial);
  const fim = parseDataLocal(item.permanencia_final);
  if (!ini && !fim) return null;
  const agora = new Date();
  if (fim && agora > fim) return "encerrado";
  if ((!ini || agora >= ini) && (!fim || agora <= fim)) return "ativo";
  return null;
};

function Collapsible({ titulo, icone: Icone, aberto, onToggle, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10" data-testid={testid}>
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between px-3 py-2.5 active:bg-white/5 rounded-xl"
        style={{ touchAction: "manipulation" }}>
        <span className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#c3c6d7" }}>
          {Icone && <Icone className="h-3.5 w-3.5" />} {titulo}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${aberto ? "rotate-180" : ""}`} style={{ color: "#8d90a0" }} />
      </button>
      {aberto && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

export default function MobilePreAgenda({ origem, eql, cpf, nome, tipo, onClose }) {
  const usuarioCadastro = `${tipo || "titular"} - ${nome}`;
  const formVazio = () => ({
    cpf: "", nome: "", ddi_celular: "+55", celular: "",
    pessoa_visitada_nome: nome || "", empresa_nome: "",
    motivo: "", permanencia_inicial: agoraLocal(), permanencia_final: hojeFim(),
    placa: "", modelo_veiculo: "", cor_veiculo: "", observacao: "",
  });

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [formAberto, setFormAberto] = useState(false);
  const [form, setForm] = useState(formVazio);
  const [editandoId, setEditandoId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [cpfErro, setCpfErro] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [openPerm, setOpenPerm] = useState(false);
  const [openVeiculo, setOpenVeiculo] = useState(false);
  const [openObs, setOpenObs] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await fetch(`${API}/public/agendamento/visitantes?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${encodeURIComponent(cpf)}`);
      const j = await r.json();
      if (j.ok) setLista(j.visitantes || []);
      else toast.error("Falha ao carregar agendamentos");
    } catch { toast.error("Sem conexão com o servidor"); }
    finally { setCarregando(false); }
  }, [origem, eql, cpf]);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function novoAgendamento() {
    setEditandoId(null);
    setForm(formVazio());
    setCpfErro(false);
    setFormAberto(true);
  }

  function editar(item) {
    setEditandoId(item.id);
    setForm({
      cpf: item.cpf ? fmtCpf(item.cpf) : "",
      nome: item.nome || "",
      ddi_celular: item.ddi_celular || "+55",
      celular: item.celular ? fmtCelular(item.celular) : "",
      pessoa_visitada_nome: item.pessoa_visitada_nome || "",
      empresa_nome: item.empresa_nome || "",
      motivo: item.motivo || "",
      permanencia_inicial: isoParaInput(item.permanencia_inicial) || agoraLocal(),
      permanencia_final: isoParaInput(item.permanencia_final) || hojeFim(),
      placa: item.placa || "", modelo_veiculo: item.modelo_veiculo || "",
      cor_veiculo: item.cor_veiculo || "", observacao: item.observacao || "",
    });
    setCpfErro(false);
    setOpenPerm(!!(item.motivo || item.permanencia_inicial));
    setOpenVeiculo(!!item.placa);
    setOpenObs(!!item.observacao);
    setFormAberto(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function salvar() {
    const cpfDigits = form.cpf.replace(/\D/g, "");
    if (cpfDigits && !cpfValido(cpfDigits)) {
      setCpfErro(true);
      toast.error("CPF do visitante inválido. Confira os dígitos.");
      return;
    }
    if ((form.permanencia_inicial && !form.permanencia_final) || (!form.permanencia_inicial && form.permanencia_final)) {
      toast.error("Informe início e fim da permanência (ou deixe ambos em branco).");
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        origem, eql, cpf_morador: cpf, usuario_cadastro: usuarioCadastro,
        cpf: cpfDigits, nome: form.nome.trim(),
        celular: form.celular.replace(/\D/g, ""), ddi_celular: form.ddi_celular || "+55",
        motivo: form.motivo.trim(),
        permanencia_inicial: form.permanencia_inicial ? `${form.permanencia_inicial}:00` : "",
        permanencia_final: form.permanencia_final ? `${form.permanencia_final}:00` : "",
        local_acesso_nome: "",
        pessoa_visitada_nome: form.pessoa_visitada_nome.trim(),
        empresa_nome: form.empresa_nome.trim(),
        placa: form.placa.trim().toUpperCase(),
        modelo_veiculo: form.modelo_veiculo.trim(), cor_veiculo: form.cor_veiculo.trim(),
        observacao: form.observacao.trim(),
      };
      const url = editandoId
        ? `${API}/public/agendamento/visitante/${editandoId}`
        : `${API}/public/agendamento/visitante`;
      const r = await fetch(url, {
        method: editandoId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!j.ok) {
        toast.error(j.motivo === "sem_permissao_api_externa"
          ? "Este agendamento não foi criado pelo aplicativo e não pode ser alterado aqui."
          : j.detalhe || "Falha ao salvar agendamento");
        return;
      }
      toast.success(editandoId ? "Agendamento atualizado" : "Agendamento criado");
      setFormAberto(false);
      setEditandoId(null);
      setForm(formVazio());
      carregar();
    } catch { toast.error("Sem conexão com o servidor"); }
    finally { setSalvando(false); }
  }

  async function excluir(item) {
    setExcluindo(true);
    try {
      const q = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${encodeURIComponent(cpf)}&usuario_cadastro=${encodeURIComponent(usuarioCadastro)}`;
      const r = await fetch(`${API}/public/agendamento/visitante/${item.id}?${q}`, { method: "DELETE" });
      const j = await r.json();
      if (!j.ok) {
        toast.error(j.motivo === "sem_permissao_api_externa"
          ? "Este agendamento não foi criado pelo aplicativo e não pode ser excluído aqui."
          : "Falha ao excluir agendamento");
        return;
      }
      toast.success("Agendamento excluído");
      setConfirmDel(null);
      carregar();
    } catch { toast.error("Sem conexão com o servidor"); }
    finally { setExcluindo(false); }
  }

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-preagenda">
      <header className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3.5"
        style={{ background: "rgba(17,19,27,0.92)", backdropFilter: "blur(12px)" }}>
        <button type="button" onClick={onClose} data-testid="appmobile-preagenda-voltar" aria-label="Voltar"
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }}
          data-testid="appmobile-preagenda-titulo">Pré Agenda</h1>
        <button type="button" onClick={carregar} data-testid="appmobile-preagenda-atualizar" aria-label="Atualizar"
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <RefreshCw className={`h-4 w-4 ${carregando ? "animate-spin" : ""}`} />
        </button>
      </header>

      <main className="px-4 pb-10 space-y-3">
        <div className="rounded-[14px] p-3" style={glassCard}>
          <button type="button" data-testid="appmobile-preagenda-novo-toggle"
            onClick={() => (formAberto ? setFormAberto(false) : novoAgendamento())}
            className="w-full flex items-center justify-between px-1 py-1"
            style={{ touchAction: "manipulation" }}>
            <span className="text-sm font-semibold" style={{ color: "#e1e2ed" }}>
              {editandoId ? "Editando agendamento" : "Novo agendamento"}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${formAberto ? "rotate-180" : ""}`} style={{ color: "#8d90a0" }} />
          </button>

          {formAberto && (
            <div className="mt-3 space-y-3" data-testid="appmobile-preagenda-form">
              <div>
                <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>CPF do visitante (opcional)</label>
                <input value={form.cpf} data-testid="appmobile-preagenda-cpf"
                  onChange={(e) => { setForm((f) => ({ ...f, cpf: fmtCpf(e.target.value) })); setCpfErro(false); }}
                  onBlur={() => setCpfErro(!!form.cpf && !cpfValido(form.cpf))}
                  placeholder="000.000.000-00" inputMode="numeric"
                  className={inputClass} style={inputStyle} />
                {cpfErro && <p className="text-[11px] mt-1" style={{ color: "#ffb4ab" }} data-testid="appmobile-preagenda-cpf-erro">CPF inválido</p>}
              </div>

              <div>
                <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Nome completo</label>
                <input value={form.nome} onChange={set("nome")} data-testid="appmobile-preagenda-nome"
                  placeholder="Nome do visitante (opcional)" className={inputClass} style={inputStyle} />
              </div>

              <div className="flex gap-2">
                <div className="w-24">
                  <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>DDI</label>
                  <input value={form.ddi_celular} data-testid="appmobile-preagenda-ddi"
                    onChange={(e) => setForm((f) => ({ ...f, ddi_celular: fmtDDI(e.target.value) }))}
                    className={inputClass} style={inputStyle} />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Celular</label>
                  <input value={form.celular} data-testid="appmobile-preagenda-celular"
                    onChange={(e) => setForm((f) => ({ ...f, celular: fmtCelular(e.target.value) }))}
                    placeholder="(11) 99999-9999" inputMode="numeric" className={inputClass} style={inputStyle} />
                </div>
              </div>

              <div>
                <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Pessoa visitada</label>
                <div className="relative">
                  <input value={form.pessoa_visitada_nome} onChange={set("pessoa_visitada_nome")}
                    data-testid="appmobile-preagenda-pessoa-visitada"
                    className={`${inputClass} pr-9`} style={inputStyle} />
                  {form.pessoa_visitada_nome && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, pessoa_visitada_nome: "" }))}
                      data-testid="appmobile-preagenda-pessoa-limpar" aria-label="Limpar"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md active:bg-white/10"
                      style={{ color: "#8d90a0" }}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Empresa</label>
                <input value={form.empresa_nome} onChange={set("empresa_nome")} data-testid="appmobile-preagenda-empresa"
                  placeholder="Empresa do visitante (opcional)" className={inputClass} style={inputStyle} />
              </div>

              <Collapsible titulo="Permanência" aberto={openPerm} onToggle={() => setOpenPerm(!openPerm)}
                testid="appmobile-preagenda-permanencia">
                <div>
                  <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Motivo da visita</label>
                  <input value={form.motivo} onChange={set("motivo")} data-testid="appmobile-preagenda-motivo"
                    placeholder="Ex.: manutenção, visita familiar" className={inputClass} style={inputStyle} />
                </div>
                <div>
                  <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Início</label>
                  <input type="datetime-local" value={form.permanencia_inicial}
                    onChange={set("permanencia_inicial")} data-testid="appmobile-preagenda-inicio"
                    className={inputClass} style={inputStyle} />
                </div>
                <div>
                  <label className="text-[11px] mb-1 block" style={{ color: "#8d90a0" }}>Fim</label>
                  <input type="datetime-local" value={form.permanencia_final}
                    onChange={set("permanencia_final")} data-testid="appmobile-preagenda-fim"
                    className={inputClass} style={inputStyle} />
                </div>
              </Collapsible>

              <Collapsible titulo="Veículo" icone={Car} aberto={openVeiculo} onToggle={() => setOpenVeiculo(!openVeiculo)}
                testid="appmobile-preagenda-veiculo">
                <input value={form.placa} onChange={(e) => setForm((f) => ({ ...f, placa: e.target.value.toUpperCase() }))}
                  placeholder="Placa" data-testid="appmobile-preagenda-placa" className={inputClass} style={inputStyle} />
                <input value={form.modelo_veiculo} onChange={set("modelo_veiculo")}
                  placeholder="Modelo" data-testid="appmobile-preagenda-modelo" className={inputClass} style={inputStyle} />
                <input value={form.cor_veiculo} onChange={set("cor_veiculo")}
                  placeholder="Cor" data-testid="appmobile-preagenda-cor" className={inputClass} style={inputStyle} />
              </Collapsible>

              <Collapsible titulo="Observação" aberto={openObs} onToggle={() => setOpenObs(!openObs)}
                testid="appmobile-preagenda-obs">
                <textarea value={form.observacao} onChange={set("observacao")} rows={3}
                  placeholder="Observações adicionais" data-testid="appmobile-preagenda-observacao"
                  className={inputClass} style={inputStyle} />
              </Collapsible>

              <button type="button" onClick={salvar} disabled={salvando}
                data-testid="appmobile-preagenda-salvar"
                className="w-full rounded-xl py-3 text-sm font-semibold text-white active:scale-[0.98] transition-transform disabled:opacity-50"
                style={{ background: "#2563EB", touchAction: "manipulation" }}>
                {salvando ? <Loader2 className="h-4 w-4 animate-spin inline" /> : editandoId ? "Salvar alterações" : "Agendar"}
              </button>
            </div>
          )}
        </div>

        {carregando && lista.length === 0 ? (
          <p className="text-center text-xs py-6" style={{ color: "#8d90a0" }}>Carregando…</p>
        ) : lista.length === 0 ? (
          <p className="text-center text-xs py-6" style={{ color: "#8d90a0" }} data-testid="appmobile-preagenda-vazio">
            Nenhum agendamento para esta unidade.
          </p>
        ) : (
          <div className="space-y-2" data-testid="appmobile-preagenda-lista">
            {lista.map((item) => (
              <div key={item.id} className="rounded-[14px] p-3" style={glassCard}
                data-testid={`appmobile-preagenda-item-${item.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: "#e1e2ed" }}>
                        {item.nome || "(sem nome)"}
                      </p>
                      {statusPeriodo(item) === "ativo" && (
                        <span data-testid={`appmobile-preagenda-ativo-${item.id}`}
                          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0"
                          style={{ background: "rgba(52,211,153,0.15)", color: "#34d399", border: "1px solid rgba(52,211,153,0.35)" }}>
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#34d399" }}></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "#34d399" }}></span>
                          </span>
                          Ativo
                        </span>
                      )}
                      {statusPeriodo(item) === "encerrado" && (
                        <span data-testid={`appmobile-preagenda-encerrado-${item.id}`}
                          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0"
                          style={{ background: "rgba(141,144,160,0.12)", color: "#8d90a0", border: "1px solid rgba(141,144,160,0.3)" }}>
                          Encerrado
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] mt-0.5" style={{ color: "#8d90a0" }}>
                      {item.cpf ? fmtCpf(item.cpf) : "Sem CPF"}
                      {item.empresa_nome ? ` · ${item.empresa_nome}` : ""}
                    </p>
                    {(item.permanencia_inicial || item.permanencia_final) && (
                      <p className="text-[11px] mt-0.5" style={{ color: "#8d90a0" }}>
                        {fmtPeriodo(item.permanencia_inicial)} → {fmtPeriodo(item.permanencia_final)}
                      </p>
                    )}
                  </div>
                  <button type="button" onClick={() => editar(item)} aria-label="Alterar"
                    data-testid={`appmobile-preagenda-editar-${item.id}`}
                    className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10"
                    style={{ color: "#8ab4ff", touchAction: "manipulation" }}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => setConfirmDel(item)} aria-label="Excluir"
                    data-testid={`appmobile-preagenda-excluir-${item.id}`}
                    className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10"
                    style={{ color: "#ffb4ab", touchAction: "manipulation" }}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {confirmDel?.id === item.id && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2"
                    data-testid={`appmobile-preagenda-confirma-${item.id}`}>
                    <span className="flex-1 text-[11px]" style={{ color: "#ffb4ab" }}>Excluir este agendamento?</span>
                    <button type="button" onClick={() => excluir(item)} disabled={excluindo}
                      data-testid={`appmobile-preagenda-confirma-sim-${item.id}`}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white active:scale-95"
                      style={{ background: "#dc2626", touchAction: "manipulation" }}>
                      {excluindo ? "…" : "Sim, excluir"}
                    </button>
                    <button type="button" onClick={() => setConfirmDel(null)}
                      data-testid={`appmobile-preagenda-confirma-nao-${item.id}`}
                      className="rounded-lg px-3 py-1.5 text-xs active:bg-white/10"
                      style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
                      Não
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
